"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DATE_ONLY_PATTERN = exports.CN_MOBILE_PATTERN = void 0;
exports.isFieldVisible = isFieldVisible;
exports.uploadMaxCount = uploadMaxCount;
exports.validateAnswers = validateAnswers;
exports.validateFieldValue = validateFieldValue;
exports.isIdCardValid = isIdCardValid;
/**
 * 表单**提交值**校验器（跨端共享）
 * -----------------------------------------------------------------------------
 * 一条规则只写一遍：**三端（server / admin / h5）引用同一份校验逻辑与同一份文案**。
 *
 * 定位分工（不得颠倒）：
 *   - 本文件是**判定逻辑**的来源，前端用它做体验优化（即时提示、禁用提交按钮）
 *   - 服务端的**最终裁决**仍然必须走一遍本文件 —— 前端绕过校验直接提交非法值，
 *     必须在服务端被拦下（`ENROLL-004`，`details.fields` 给出字段级原因）
 *
 * 因此「验证通过」在被服务端复算之前，永远只是**体验**，不是**保证**。
 *
 * 校验顺序与语义：
 *   1. **未知字段**直接拒绝（schema 里没有的键 = 客户端在编字段）
 *   2. **条件隐藏**的字段一律跳过校验（隐藏即不参与必填；已填的也忽略，不报错）
 *   3. 其余字段按类型校验 + 声明式规则（pattern / 长度 / 范围）
 *   4. `phone` / `idcard` 走内置校验（正则 + 身份证校验位），不依赖 Schema 配置
 *   5. `region` 是**复合值**（三级行政区划码 + 详细地址），在标量空值判定**之前**单独分流
 *
 * 任务卡：T-06（提取至 shared 以支撑两端渲染器）
 */
const form_schema_js_1 = require("./form-schema.js");
const region_js_1 = require("./region.js");
exports.CN_MOBILE_PATTERN = /^1[3-9]\d{9}$/;
exports.DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
/** 条件显示是否成立（§3.4：equals / notEquals / in，仅单条件） */
function isFieldVisible(field, answers) {
    const condition = field.visibleWhen;
    if (!condition)
        return true;
    const actual = answers[condition.key];
    if (condition.equals !== undefined)
        return actual === condition.equals;
    if (condition.notEquals !== undefined)
        return actual !== condition.notEquals;
    if (Array.isArray(condition.in))
        return condition.in.includes(actual);
    return true;
}
/**
 * `upload` 字段的**有效张数上限**（单份字段恒为 1）。
 *
 * 三端必须引用同一份换算：上传器拿它算剩余额度、校验器拿它拦超量、
 * 后台配置页拿它回显。任何一端自己写 `field.maxCount ?? 5` 都可能把
 * 脏配置（0 / 负数 / 超过硬上限）当成有效值，导致「明明没到 5 张却提示已满」。
 */
function uploadMaxCount(field) {
    if (field.multiple !== true)
        return 1;
    const configured = field.maxCount;
    if (typeof configured !== 'number' || !Number.isFinite(configured)) {
        return form_schema_js_1.UPLOAD_DEFAULT_MAX_COUNT;
    }
    const floored = Math.floor(configured);
    if (floored < 1)
        return 1;
    return Math.min(floored, form_schema_js_1.UPLOAD_MAX_COUNT_LIMIT);
}
/**
 * 逐字段校验并返回**全部**错误（不是遇错即抛）——
 * 让学员一次性看到所有问题，而不是改一条报一条。
 */
function validateAnswers(fields, answers, options) {
    const errors = [];
    const known = new Set(fields.map((field) => field.key));
    for (const key of Object.keys(answers)) {
        if (!known.has(key)) {
            errors.push({
                key,
                message: form_schema_js_1.FIELD_KEY_PATTERN.test(key) ? '表单中不存在该字段' : '字段名不合法',
            });
        }
    }
    for (const field of fields) {
        if (!isFieldVisible(field, answers))
            continue;
        errors.push(...validateField(field, answers[field.key], options?.partial === true));
    }
    return errors;
}
/** 单个字段的校验入口（渲染器在 `onBlur` 时用它做即时提示） */
function validateFieldValue(field, value, partial = false) {
    return validateField(field, value, partial);
}
function validateField(field, value, partial) {
    const custom = field.validation?.message;
    const fail = (message) => [{ key: field.key, message: custom ?? message }];
    // `region` 是**复合值**（三级码 + 详细地址），不能走下面「空值」的标量判定：
    // 一个 `{provinceCode:'',…,detail:''}` 的对象并非 `''`，会被误判成已填。
    if (field.type === form_schema_js_1.FieldType.REGION)
        return validateRegionField(field, value, partial, fail);
    const empty = value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0);
    if (empty) {
        if (partial)
            return [];
        return field.required ? fail('必填项未填写') : [];
    }
    switch (field.type) {
        case form_schema_js_1.FieldType.TEXT:
        case form_schema_js_1.FieldType.TEXTAREA: {
            if (typeof value !== 'string')
                return fail('必须是文本');
            if (field.type === form_schema_js_1.FieldType.TEXTAREA) {
                // 多行文本只约束长度，不约束 pattern（换行会破坏大部分正则直觉）
                return checkLength(field, value, fail);
            }
            return [...checkLength(field, value, fail), ...checkPattern(field, value, fail)];
        }
        case form_schema_js_1.FieldType.NUMBER: {
            if (typeof value !== 'number' || !Number.isFinite(value))
                return fail('必须是数字');
            const { min, max } = field.validation ?? {};
            if (min !== undefined && value < min)
                return fail(`不能小于 ${min}`);
            if (max !== undefined && value > max)
                return fail(`不能大于 ${max}`);
            return [];
        }
        case form_schema_js_1.FieldType.DATE: {
            if (typeof value !== 'string' || !exports.DATE_ONLY_PATTERN.test(value))
                return fail('日期格式应为 YYYY-MM-DD');
            const parsed = new Date(`${value}T00:00:00Z`);
            if (Number.isNaN(parsed.getTime()))
                return fail('日期不存在');
            return [];
        }
        case form_schema_js_1.FieldType.SELECT:
        case form_schema_js_1.FieldType.RADIO: {
            if (typeof value !== 'string')
                return fail('必须是选项值');
            const allowed = (field.options ?? []).map((option) => option.value);
            return allowed.includes(value) ? [] : fail('不是可选项');
        }
        case form_schema_js_1.FieldType.CHECKBOX: {
            if (!Array.isArray(value))
                return fail('必须是数组');
            const allowed = new Set((field.options ?? []).map((option) => option.value));
            if (value.some((item) => typeof item !== 'string' || !allowed.has(item))) {
                return fail('包含不可选项');
            }
            const unique = new Set(value);
            if (unique.size !== value.length)
                return fail('存在重复选项');
            return [];
        }
        case form_schema_js_1.FieldType.BOOLEAN: {
            return typeof value === 'boolean' ? [] : fail('必须是布尔值');
        }
        case form_schema_js_1.FieldType.UPLOAD: {
            // 一期只收存储 key（不接收可达 URL，见 07-留存与合规）
            if (field.multiple !== true) {
                // 单份字段**必须**是字符串：收到数组一律拒绝，而不是「取第一个」——
                // 静默降级会让学员以为传了 3 份、后台却只有 1 份，比报错难查得多。
                if (Array.isArray(value))
                    return fail('该字段只接受一个文件');
                if (typeof value !== 'string')
                    return fail('上传结果格式非法');
                return value.trim() === '' ? fail('上传结果无效') : [];
            }
            // 多份字段：接受字符串是为了兼容「字段由单份改成多份」之前的存量答案，
            // 存量值是单个 key，语义上等价于只有一份。
            if (typeof value === 'string')
                return value.trim() === '' ? fail('上传结果无效') : [];
            if (!Array.isArray(value))
                return fail('上传结果格式非法');
            if (value.some((item) => typeof item !== 'string' || item.trim() === '')) {
                return fail('上传结果格式非法');
            }
            const maxCount = uploadMaxCount(field);
            if (value.length > maxCount)
                return fail(`最多上传 ${maxCount} 份`);
            // 去重：同一个文件重复引用只算一份，没必要在库里存两遍同样的指向
            if (new Set(value).size !== value.length)
                return fail('存在重复文件');
            return [];
        }
        case form_schema_js_1.FieldType.PHONE: {
            if (typeof value !== 'string')
                return fail('必须是手机号');
            return exports.CN_MOBILE_PATTERN.test(value) ? [] : fail('手机号格式不正确');
        }
        case form_schema_js_1.FieldType.IDCARD: {
            if (typeof value !== 'string')
                return fail('必须是身份证号');
            return isIdCardValid(value) ? [] : fail('身份证号校验未通过');
        }
        case form_schema_js_1.FieldType.SIGNATURE: {
            // 手写签名（C-23）：答案 = 签名图资产的 assetId（字符串），与 upload 单文件同形状。
            // 签名只能整幅重签，不存在「半个签名」，故不认数组。
            if (typeof value === 'string')
                return value.trim() === '' ? fail('签名结果无效') : [];
            return fail('签名结果格式非法');
        }
        default:
            // 定义校验已挡住不支持的类型；走到这里说明 Schema 在库里被改坏了
            return fail('字段类型不受支持');
    }
}
/**
 * `region` 字段（行政区划三级 + 详细地址）的判定。
 *
 * 口径：
 *   1. **未动过**（三级全空且详细地址为空）→ 只受 `required` 约束；
 *   2. **填了一半**（码不完整 / 有详细地址却三级全空）→ **提交时一律拦下**。
 *      半截地址会直接破坏「客服码按区域匹配」——匹配不到就退化成缺省码，
 *      学员看到的是错的客服，比报错更难发现；
 *   3. **不设区的市**（东莞 / 中山 / 嘉峪关 / 儋州）只到市级即算完整
 *      —— 由 `isRegionComplete` 统一判定，此处不重复实现；
 *   4. **草稿模式（`partial`）允许填一半**：完整性与「详细地址必填」本质是
 *      **必填类**要求，与「表单填一半退出 → 存草稿」口径一致，到提交再拦；
 *      但**超长属格式问题，草稿也照拦**（与标量字段的 `checkLength` 同口径）。
 */
function validateRegionField(field, value, partial, fail) {
    const region = (0, region_js_1.normalizeRegionValue)(value);
    const hasDetail = (0, region_js_1.isRegionDetailFilled)(region);
    const touched = Boolean(region.provinceCode) || hasDetail;
    const max = field.validation?.maxLength ?? region_js_1.REGION_DETAIL_MAX_LENGTH;
    if (region.detail.length > max)
        return fail(`详细地址长度不能超过 ${max}`);
    if (!touched) {
        if (partial)
            return [];
        return field.required ? fail('请选择所在地区并填写详细地址') : [];
    }
    if (partial)
        return [];
    if (!(0, region_js_1.isRegionComplete)(region))
        return fail('省 / 市 / 区 未选完整');
    if (!hasDetail)
        return fail('请填写详细地址');
    return [];
}
function checkLength(field, value, fail) {
    const { minLength, maxLength } = field.validation ?? {};
    if (minLength !== undefined && value.length < minLength)
        return fail(`长度不能少于 ${minLength}`);
    if (maxLength !== undefined && value.length > maxLength)
        return fail(`长度不能超过 ${maxLength}`);
    return [];
}
function checkPattern(field, value, fail) {
    const { pattern } = field.validation ?? {};
    if (!pattern)
        return [];
    try {
        return new RegExp(pattern).test(value) ? [] : fail('格式不正确');
    }
    catch {
        // 定义侧的合法性已在保存时校验；此处仅防御库内被改坏的正则，不让它 500
        return fail('格式校验规则无效');
    }
}
/**
 * 身份证号校验：18 位 + GB 11643 校验位（mod 11-2）。
 *
 * 放在 shared 而非某一端：算法必须**三端字面相同** —— 前端判「通过」而服务端判「不通过」
 * 会表现为「明明填对了却提交失败」，是最难排查的一类缺陷。
 * 前端做体验、服务端做判定，但判定依据必须是同一份代码。
 */
function isIdCardValid(value) {
    const normalized = value.trim().toUpperCase();
    if (!/^\d{17}[\dX]$/.test(normalized))
        return false;
    // 出生日期段必须真实存在（挡住 20260231 这类能过校验位的号）
    const year = Number(normalized.slice(6, 10));
    const month = Number(normalized.slice(10, 12));
    const day = Number(normalized.slice(12, 14));
    const date = new Date(Date.UTC(year, month - 1, day));
    if (date.getUTCFullYear() !== year ||
        date.getUTCMonth() !== month - 1 ||
        date.getUTCDate() !== day) {
        return false;
    }
    const weights = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2];
    const checkChars = ['1', '0', 'X', '9', '8', '7', '6', '5', '4', '3', '2'];
    let sum = 0;
    for (let i = 0; i < 17; i += 1)
        sum += Number(normalized[i]) * weights[i];
    return checkChars[sum % 11] === normalized[17];
}
//# sourceMappingURL=form-validator.js.map